import type { ReportSectionMeta } from "~/api/model";

export const TERMINAL_SECTION_STATUSES = ["completed", "failed"] as const;
const PERCENTAGE_SCALE = 100;
const MIN_ETA_OBSERVATIONS = 2;
const MILLISECONDS_PER_MINUTE = 60_000;

type TerminalSectionStatus = (typeof TERMINAL_SECTION_STATUSES)[number];

export type CompletionObservation = {
  doneCount: number;
  timestamp: number;
};

export type ReportProgress = {
  completedCount: number;
  failedCount: number;
  totalCount: number;
  percentage: number;
  hasFailed: boolean;
  isComplete: boolean;
};

export const isTerminalSectionStatus = (
  status: string
): status is TerminalSectionStatus =>
  status === "completed" || status === "failed";

export const getReportProgress = (
  sections: ReadonlyArray<Pick<ReportSectionMeta, "status">>
): ReportProgress => {
  const completedCount = sections.filter(
    (section) => section.status === "completed"
  ).length;
  const failedCount = sections.filter(
    (section) => section.status === "failed"
  ).length;
  const totalCount = sections.length;
  const doneCount = completedCount + failedCount;

  return {
    completedCount,
    failedCount,
    totalCount,
    percentage:
      totalCount === 0
        ? 0
        : Math.round((doneCount / totalCount) * PERCENTAGE_SCALE),
    hasFailed: failedCount > 0,
    isComplete:
      totalCount > 0 &&
      sections.every((section) => isTerminalSectionStatus(section.status)),
  };
};

export const getEstimatedRemainingMinutes = (
  observations: ReadonlyArray<CompletionObservation>,
  remainingSections: number
): number | null => {
  if (observations.length < MIN_ETA_OBSERVATIONS || remainingSections <= 0) {
    return null;
  }

  const firstObservation = observations[0];
  const lastObservation = observations[observations.length - 1];
  const elapsedMilliseconds =
    lastObservation.timestamp - firstObservation.timestamp;
  const observedSections =
    lastObservation.doneCount - firstObservation.doneCount;

  if (elapsedMilliseconds <= 0 || observedSections <= 0) {
    return null;
  }

  const millisecondsPerSection = elapsedMilliseconds / observedSections;
  return Math.floor(
    (millisecondsPerSection * remainingSections) / MILLISECONDS_PER_MINUTE
  );
};
